import { useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { companyCurrentIsoCountry } from 'modules/core/lib/companyCurrentIsoCountry'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'

import { companyMainLocation } from 'modules/locations/store/collections/locations'
import useCallPageEvents from 'modules/onboarding/hooks/useCallPageEvents'
import useHubspotVariables from 'modules/onboarding/hooks/useHubspotVariables'
import callRequests from 'modules/onboarding/store/collections/callRequests'
import CallRequest from 'modules/onboarding/store/models/CallRequest'
import QualificationEntry from 'modules/onboarding/store/models/QualificationEntry'

import { BookingForm, CallForm } from './Forms'
import { BookingRequestForm, CallRequestForm, CallRequestValues } from './Forms/forms'
import Option from './Option'
import Thankyou from './Thankyou'
import { getUrl } from './utils'

const CallPageModal = ({ onClose }: { onClose: () => void }) => {
  const current = companies.current
  const location = companyMainLocation()
  const phoneNumber = location.attributes.phone_number
  const [isBookingCall, setIsBookingCall] = useState(false)
  const [success, setSuccess] = useState(false)
  const { contactPhone } = useHubspotVariables()
  const { eventEmitter } = useCallPageEvents({
    from: 'call_request_modal',
    callType: isBookingCall ? 'scheduled' : 'direct',
  })
  const submitCta = isBookingCall
    ? i18n.t('pricing_plans.help_info.callpage_modal.schedule_title')
    : i18n.t('pricing_plans.help_info.callpage_modal.call_title')
  const cancelCta = success
    ? i18n.t('pricing_plans.help_info.callpage_modal.close_button')
    : i18n.t('pricing_plans.help_info.callpage_modal.cancel_button')

  const callForm = useLazyRef(() => new CallRequestForm({ phone: phoneNumber })).current

  const bookingForm = useLazyRef(
    () =>
      new BookingRequestForm({
        phone: phoneNumber,
        date: null,
        time: null,
      })
  ).current

  const scheduleCallRequest = async (body: CallRequestValues) => {
    const { success } = await asyncAction(
      () => {
        return callRequests.create(body)
      },
      {
        errorFlash: false,
        successFlash: false,
      }
    )
    if (success) {
      setSuccess(true)
      eventEmitter(trackingEvents.onboarding.requestCall)
    }
  }

  const createCallZap = async (body: CallRequestValues) => {
    const isoCountryCode = companyCurrentIsoCountry(current)
    const url = getUrl(isoCountryCode)
    const createCall = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(body),
    }).then((res) => res.json())

    if (createCall.status === 'success') {
      setSuccess(true)
      eventEmitter(trackingEvents.onboarding.requestCall)
    }
  }

  const { email } = useCurrentSession()

  const onSubmit = async () => {
    const form = isBookingCall ? bookingForm : callForm
    if (!form.isValid) return

    const body = {
      phone: form.values.phone.startsWith('+') ? form.values.phone : `+${form.values.phone}`,
      scheduled_call: '',
      request_call: new Date(),
      email: email || '',
      marketing_lead_form_campaign: QualificationEntry.Campaigns.callPage,
      hs_lead_status: CallRequest.HubspotLead.status,
      lead_birthplace: CallRequest.HubspotLead.leadBirthplace,
      lead_origin: CallRequest.HubspotLead.leadOrigin,
    }

    if (isBookingCall) {
      const { date, time } = form.values
      const datetime = new Date(`${date} ${time}`)
      body.scheduled_call = moment(datetime).format('YYYY-MM-DDTHH:mm:ssZ')
      await scheduleCallRequest(body)
    } else {
      await createCallZap(body)
    }
  }

  return (
    <Modal
      illustration="contact-us"
      title={i18n.t('pricing_plans.help_info.callpage_modal.title')}
      description={i18n.t('pricing_plans.help_info.callpage_modal.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Box flexDirection="row" gap={'s8'}>
          <Button type="secondary" onClick={() => onClose()}>
            {cancelCta}
          </Button>
          {!success && <Button submit>{submitCta}</Button>}
        </Box>
      }
    >
      {success ? (
        <Thankyou />
      ) : contactPhone && phoneNumber ? (
        <Box gap={'s24'}>
          <Option
            title={i18n.t('pricing_plans.help_info.callpage_modal.call_title')}
            description={i18n.t('pricing_plans.help_info.callpage_modal.call_description', {
              time_zone: contactPhone.timeZone,
            })}
            isChecked={!isBookingCall}
            setIsChecked={() => setIsBookingCall(false)}
          >
            <CallForm form={callForm} numberPlaceHolder={phoneNumber} />
          </Option>
          <Option
            title={i18n.t('pricing_plans.help_info.callpage_modal.schedule_title')}
            description={i18n.t('pricing_plans.help_info.callpage_modal.schedule_description')}
            isChecked={isBookingCall}
            setIsChecked={() => setIsBookingCall(true)}
          >
            <BookingForm form={bookingForm} numberPlaceHolder={phoneNumber} />
          </Option>
        </Box>
      ) : null}
    </Modal>
  )
}

export default CallPageModal
